import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollToId, useLang } from "@/i18n";
import ParticleField from "@/components/ParticleField";

const ease = [0.16, 1, 0.3, 1];

export default function Hero() {
  const { t } = useLang();
  const h = t.hero;

  return (
    <section id="inicio" data-testid="hero-section" className="relative flex min-h-[92vh] items-end overflow-hidden sm:min-h-screen sm:items-center">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_70%_20%,rgba(255,0,122,0.25)_0%,transparent_55%)]" />
      <ParticleField />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-20 pt-32 sm:px-8 sm:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease }}
          className="mb-6 flex flex-wrap items-center gap-3"
        >
          <img
            src="/logo-hero.webp"
            alt="Logo de K-House Dance Academy"
            data-testid="hero-logo"
            className="h-14 w-auto drop-shadow-[0_0_18px_rgba(255,0,122,0.35)] sm:h-16"
          />
        </motion.div>

        <motion.span
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease }}
          className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/25 bg-black/40 px-4 py-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.25em] text-white backdrop-blur-md"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute h-2 w-2 rounded-full bg-accent animate-ping-soft" />
            <span className="relative h-2 w-2 rounded-full bg-accent" />
          </span>
          {h.tag}
        </motion.span>

        <h1 className="font-display font-black uppercase leading-[0.9] tracking-tight">
          <span className="block overflow-hidden">
            <motion.span
              className="block text-[clamp(2.6rem,8vw,6.5rem)] text-white drop-shadow-[0_4px_30px_rgba(0,0,0,0.6)]"
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.9, delay: 0.15, ease }}
            >
              {h.titleA}
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span
              className="block text-[clamp(1.5rem,4.2vw,3.4rem)] text-outline-pink"
              initial={{ y: "110%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 0.9, delay: 0.28, ease }}
            >
              {h.titleB}
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5, ease }}
          className="mt-5 max-w-xl font-display text-sm font-extrabold uppercase tracking-[0.12em] text-white sm:text-lg"
        >
          {h.tagline}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.65, ease }}
          className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <button
            data-testid="hero-know-us-button"
            onClick={() => scrollToId("academia")}
            className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/70 bg-black/30 px-9 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em] text-white backdrop-blur-md transition-all duration-300 hover:border-accent hover:bg-accent hover:text-white"
          >
            {h.ctaKnow}
            <ArrowUpRight size={18} />
          </button>
          <button
            data-testid="hero-enrollment-button"
            onClick={() => scrollToId("contacto")}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-accent px-9 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em] text-white shadow-[0_0_30px_rgba(255,0,122,0.5)] transition-all duration-300 hover:scale-[1.03] hover:bg-accent-hover hover:shadow-[0_0_46px_rgba(255,0,122,0.7)]"
          >
            {h.ctaEnroll}
            <ArrowUpRight size={18} />
          </button>
        </motion.div>
      </div>

      <button
        data-testid="hero-scroll-hint"
        onClick={() => scrollToId("grupos")}
        className="absolute bottom-6 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 text-zinc-400 transition-colors hover:text-accent sm:flex"
      >
        <span className="font-mono text-[10px] font-bold uppercase tracking-[0.35em]">{h.scroll}</span>
        <ArrowDown size={16} className="animate-bounce" />
      </button>
    </section>
  );
}